/*!
 * KoliBri - The accessible HTML-Standard
 */
import { c as createPropDefinition, n as normalizeString, b as normalizeBoolean, d as normalizeObject, a as normalizeInteger } from './normalizers-BNeak4hj.js';
import { k as a11yHint, j as devHint } from './common-D-vggmh2.js';
import { h as hrefProp } from './href-B7u09W3s.js';
import { l as labelWithExpertSlotProp } from './label-with-expert-slot-CU-oeLnm.js';
import './variant-quote-ClYWonZq.js';
import { a as alignPropTypeOptions } from './align-C8fl12z_.js';
import { h } from './index-C_ZKddLG.js';
import { t as translate } from './i18n-CvFPh0g5.js';
import { c as classNameFromVariant } from './variant-class-name-9qZ5egaq.js';
import { c as clsx } from './clsx-COFh-Vc8.js';
import { b as bem } from './bem-registry-3ondDImf.js';
import { I as IconFC } from './component-BjVFpeYY.js';
import { S as SpanFC } from './component-DepnuZGT.js';
import { T as TooltipFC } from './component-BXCnkVUN.js';

const accessKeyProp = createPropDefinition('accessKey', '', normalizeString);

const ariaControlsProp = createPropDefinition('ariaControls', '', normalizeString);

const ARIA_CURRENT_VALUE_OPTIONS = ['date', 'location', 'page', 'step', 'time', 'true', 'false'];
function normalizeAriaCurrentValue(value) {
    const str = normalizeString(value);
    return ARIA_CURRENT_VALUE_OPTIONS.includes(str) ? str : 'page';
}
const ariaCurrentValueProp = createPropDefinition('ariaCurrentValue', 'page', normalizeAriaCurrentValue);

const ariaDescriptionProp = createPropDefinition('ariaDescription', '', normalizeString);

function normalizeAriaExpanded(value) {
    if (value === true || value === 'true') {
        return 'true';
    }
    if (value === false || value === 'false') {
        return 'false';
    }
    return '';
}
const ariaExpandedProp = createPropDefinition('ariaExpanded', '', normalizeAriaExpanded);

const ariaOwnsProp = createPropDefinition('ariaOwns', '', normalizeString);

const SAFE_CLASS_NAME_RE = /^[a-zA-Z][a-zA-Z0-9_-]{0,60}$/;
const isSafeClassName = (value) => typeof value === 'string' && SAFE_CLASS_NAME_RE.test(value);

function validateCustomClass(value) {
    return value === '' || isSafeClassName(value);
}
const customClassProp = createPropDefinition('customClass', '', normalizeString, validateCustomClass);

const disabledProp = createPropDefinition('disabled', false, normalizeBoolean);

const downloadProp = createPropDefinition('download', undefined, normalizeString);

const hideLabelProp = createPropDefinition('hideLabel', false, normalizeBoolean);

const inlineProp = createPropDefinition('inline', true, normalizeBoolean);

function normalizeLinkCallbacks(value) {
    if (typeof value === 'object' && value !== null) {
        return value;
    }
    throw new Error(`Invalid on callbacks: expected object, got ${typeof value}`);
}
const linkCallbacksProp = createPropDefinition('on', {}, normalizeLinkCallbacks);

const LINK_ROLE_OPTIONS = ['tab', 'treeitem'];
function normalizeLinkRole(value) {
    return typeof value === 'string' && LINK_ROLE_OPTIONS.includes(value) ? value : '';
}
const linkRoleProp = createPropDefinition('role', '', normalizeLinkRole);

const linkTargetProp = createPropDefinition('target', '', normalizeString);

const shortKeyProp = createPropDefinition('shortKey', '', normalizeString);

function normalizeSpanIcons(value) {
    if (!value || (typeof value === 'string' && value === '')) {
        return {};
    }
    if (typeof value === 'string') {
        return normalizeString(value);
    }
    if (typeof value === 'object') {
        return normalizeObject(value);
    }
    throw new Error(`Invalid icons: ${typeof value}`);
}
function validateSpanIcons(value) {
    if (typeof value === 'string') {
        return value.length > 0;
    }
    return typeof value === 'object' && value !== null;
}
const spanIconsProp = createPropDefinition('icons', {}, normalizeSpanIcons, validateSpanIcons);

const tabIndexProp = createPropDefinition('tabIndex', 0, normalizeInteger, undefined, {
    hints: (_propName, value) => {
        if (typeof value === 'number' && value > 0) {
            a11yHint(`Positive tabIndex values ("${value}") can disrupt the natural tab order. Use 0 for tabbable elements or rely on DOM order.`);
        }
    },
});

function normalizeTooltipAlign(value) {
    const str = normalizeString(value);
    if (alignPropTypeOptions.includes(str)) {
        return str;
    }
    return 'right';
}
const tooltipAlignProp = createPropDefinition('tooltipAlign', 'right', normalizeTooltipAlign, (v) => alignPropTypeOptions.includes(v));

function normalizeVariant(value) {
    if (Array.isArray(value)) {
        return value;
    }
    if (typeof value === 'string') {
        return value.split(' ');
    }
    return [];
}
function validateVariant(value) {
    return value.length === 0 || value.every(isSafeClassName);
}
const variantProp = createPropDefinition('variant', [], normalizeVariant, validateVariant);

const linkPropsConfig = {
    required: [hrefProp],
    optional: [
        accessKeyProp,
        ariaControlsProp,
        ariaCurrentValueProp,
        ariaDescriptionProp,
        ariaExpandedProp,
        ariaOwnsProp,
        customClassProp,
        disabledProp,
        downloadProp,
        hideLabelProp,
        spanIconsProp,
        inlineProp,
        labelWithExpertSlotProp,
        linkCallbacksProp,
        linkRoleProp,
        linkTargetProp,
        shortKeyProp,
        tabIndexProp,
        tooltipAlignProp,
        variantProp,
    ],
};

const BemRootNodeFC = ({ block, modifiers, class: hostClass }, children) => {
    const blockBem = bem.forBlock(block);
    return h("div", { class: clsx(blockBem(modifiers), hostClass) }, children);
};

const LinkFC = (props) => {
    const { accessKey, ariaControls, ariaCurrent, ariaDescription, ariaDescriptionId, ariaExpanded, ariaOwns, customClass, disabled, download, handleAnchorClick, hideLabel, href, icons, inline, label, on, refAnchor, refTooltip, role, shortKey, tabIndex, target, variant, expertSlot, } = props;
    const translateOpenLinkInTab = translate('kol-open-link-in-tab');
    const isExternal = typeof target === 'string' && target.length > 0 && target !== '_self';
    const tagAttrs = {
        href: typeof href === 'string' && href.length > 0 ? href : 'javascript:void(0);',
        target: typeof target === 'string' && target.length > 0 ? target : undefined,
        rel: isExternal ? 'noopener' : undefined,
        download: typeof download === 'string' ? download : undefined,
    };
    if (hideLabel === true && !label) {
        devHint(`[KolLink] An aria-label must be set when _hide-label is set.`);
    }
    const trimmedAriaDescription = ariaDescription === null || ariaDescription === void 0 ? void 0 : ariaDescription.trim();
    const roleValue = role || undefined;
    return (h(BemRootNodeFC, { block: "kol-link", class: clsx({
            [customClass]: variant.includes('custom') && customClass.length > 0,
            [classNameFromVariant(variant, 'link')]: variant.length > 0,
        }), modifiers: {
            disabled: disabled === true,
            'external-link': isExternal,
            'hide-label': hideLabel === true,
            inline: inline === true,
            standalone: inline === false,
        } }, h("a", Object.assign({ ref: refAnchor }, tagAttrs, { accessKey: accessKey || undefined, "aria-current": ariaCurrent || undefined, "aria-controls": ariaControls || undefined, "aria-describedby": trimmedAriaDescription ? ariaDescriptionId : undefined, "aria-disabled": disabled ? 'true' : undefined, "aria-expanded": ariaExpanded === '' ? undefined : ariaExpanded, "aria-owns": ariaOwns || undefined, "aria-label": hideLabel && typeof label === 'string' ? `${label}${isExternal ? ` (${translateOpenLinkInTab})` : ''}` : undefined, "aria-keyshortcuts": shortKey || undefined, class: "kol-link__anchor" }, on, { onClick: handleAnchorClick, role: roleValue, tabIndex: disabled ? -1 : tabIndex }), h(SpanFC, { class: "kol-link__text", badgeText: accessKey || shortKey, icons: icons, hideLabel: hideLabel, label: expertSlot ? '' : label || href }, h("slot", { name: "expert", slot: "expert" })), isExternal && (h(IconFC, { class: "kol-link__icon", label: hideLabel ? '' : translateOpenLinkInTab, icons: 'kolicon-link-external', "aria-hidden": hideLabel }))), hideLabel === true && !expertSlot && (h("div", { class: "kol-link__tooltip" }, h(TooltipFC, { badgeText: accessKey || shortKey || '', label: typeof label === 'string' ? label : typeof href === 'string' ? href : '', refFloating: refTooltip }))), trimmedAriaDescription && (h("span", { class: "visually-hidden", id: ariaDescriptionId }, trimmedAriaDescription))));
};

export { LinkFC as L, accessKeyProp as a, ariaControlsProp as b, ariaCurrentValueProp as c, ariaDescriptionProp as d, ariaExpandedProp as e, disabledProp as f, downloadProp as g, hideLabelProp as h, inlineProp as i, linkCallbacksProp as j, shortKeyProp as k, linkPropsConfig as l, linkTargetProp as m, ariaOwnsProp as n, customClassProp as o, linkRoleProp as p, tabIndexProp as q, spanIconsProp as s, tooltipAlignProp as t, variantProp as v };
//# sourceMappingURL=component-BDS_iE5Q.js.map

//# sourceMappingURL=component-BDS_iE5Q.js.map